/**
 * Sentry Events Utility for Checkout
 * Централизованный модуль для отправки событий Sentry.
 *
 * Каждое событие содержит:
 * - человекочитаемый идентификатор (fingerprint)
 * - id пользователя (window.uid или window.orderSession)
 * - country — страна пользователя
 * - checkout_scenario — сценарий чекаута
 * - timestamp
 * - контекстные поля, специфичные для события
 *
 * УСЛОВИЯ ОТПРАВКИ СОБЫТИЙ:
 *
 * Autocomplete:
 * - empty_result: пустой ответ/таймаут от Google Autocomplete
 * - missing_required_fields: автокомплит вернул данные без обязательных полей
 * - city_modal_shown: показ модалки смены города (level=error при showCount>=3)
 *
 * Delivery API:
 * - request_error: ошибка сети или API (catch блок, response.data.error)
 * - duplicate_request: >=3 запросов к одному endpoint за 5 сек (level=error при count>=5)
 *
 * Payment:
 * - provider_init_error: ошибка инициализации платёжного провайдера
 * - transaction_failed_technical_postOrder: техническая ошибка при создании заказа
 *
 * Order:
 * - validation_failed_postOrder: бэкенд отклонил заказ ошибкой в теле HTTP 200 ответа
 *   (валидация полей/профиля: ORDER_ZIP, PROFILE_CREATION_ERROR, ...)
 *
 * Behavior:
 * - address_edited_repeatedly: редактирование адреса 50+ раз за сессию (отправляется 1 раз при достижении 50)
 * - long_time_on_address_step: >25 минут на шаге адреса (level=warning при >=180 сек)
 *
 * Profile:
 * - address_empty: пустые поля адреса в PROFILE_LAST при загрузке
 */

import * as Sentry from '@sentry/vue'
import { trackCheckoutError } from './checkoutAnalytics'

// Маппинг namespace'а eventId → step для checkout_error аналитики.
// checkout.delivery_api.* / checkout.autocomplete.* / checkout.profile.* → 'delivery'
// checkout.payment.* / checkout.order.* → 'payment'
// остальное → null (пусть BI решает по error_type)
function getStepFromEventId(eventId) {
  if (!eventId) return null
  if (
    eventId.startsWith('checkout.payment.') ||
    eventId.startsWith('checkout.order.')
  )
    return 'payment'
  if (
    eventId.startsWith('checkout.delivery_api.') ||
    eventId.startsWith('checkout.autocomplete.') ||
    eventId.startsWith('checkout.profile.')
  )
    return 'delivery'
  return null
}

/**
 * Извлекает контекст checkout из Vuex store и window
 * @param {Object} store - Vuex store
 * @returns {Object} { userId, sessionId, country, checkoutScenario }
 */
export function getCheckoutContext(store) {
  // window.uid — ID авторизованного ЧК, пустая строка если неавторизован
  const userId = window.uid || null
  // window.orderSession — session_id() из Битрикса
  const sessionId = window.orderSession || null

  return {
    userId,
    sessionId,
    country: store?.state?.country?.code || 'unknown',
    checkoutScenario: store?.state?.checkoutScenario || 'unknown',
    webview: !!store?.state?.isWebview,
    vpnBanner:
      typeof document !== 'undefined' &&
      document.querySelector('.vpn_message__container') !== null,
  }
}

/**
 * Базовая функция для отправки событий checkout
 * @param {Object} params
 * @param {string} params.eventId - человекочитаемый идентификатор события
 * @param {string} [params.level='info'] - уровень: 'info' | 'warning' | 'error'
 * @param {Object} params.context - контекст checkout { userId, sessionId, country, checkoutScenario }
 * @param {Object} [params.extraFields={}] - дополнительные поля события
 */
export function captureCheckoutEvent({
  eventId,
  level = 'info',
  context = {},
  extraFields = {},
}) {
  const { userId, sessionId, country, checkoutScenario, webview, vpnBanner } =
    context

  Sentry.withScope((scope) => {
    // Fingerprint для группировки событий
    scope.setFingerprint([eventId])

    // Теги для фильтрации в Sentry
    scope.setTag('event_id', eventId)
    scope.setTag('country', country)
    scope.setTag('checkout_scenario', checkoutScenario)
    scope.setTag('webview', String(Boolean(webview)))
    scope.setTag('vpn_banner', String(Boolean(vpnBanner)))

    // User context для построения цепочки по сессии
    scope.setUser({
      id: userId || sessionId || 'anonymous',
      user_id: userId || null,
      session_id: sessionId || null,
      is_authenticated: Boolean(userId),
      country,
    })

    // Timestamp
    scope.setExtra('timestamp', new Date().toISOString())

    // Дополнительные поля
    Object.entries(extraFields).forEach(([key, value]) => {
      if (key.startsWith('tag:')) {
        scope.setTag(key.slice(4), value)
      } else {
        scope.setExtra(key, value)
      }
    })

    const sentryEventId = Sentry.captureMessage(eventId, level)

    // Дублируем в аналитику только ошибки (level='error'):
    // batch_ordering_id / user_status подставит pushEvent в checkoutAnalytics.
    if (level === 'error') {
      trackCheckoutError({
        step: getStepFromEventId(eventId),
        errorType: eventId,
        errorMessage:
          extraFields?.error_message ||
          extraFields?.error_code ||
          extraFields?.failure_reason ||
          null,
        sentryEventId,
      })
    }
  })
}

// ===========================================
// GOOGLE AUTOCOMPLETE EVENTS
// ===========================================

/**
 * checkout.autocomplete.empty_result
 * Автокомплит не вернул результат — пустой ответ или таймаут.
 *
 * @param {Object} params
 * @param {Object} params.context - контекст checkout { memberId, country, checkoutScenario }
 * @param {string} params.query - что вводил пользователь
 * @param {number} params.responseTimeMs - время ответа в мс
 * @param {string} params.errorType - 'empty' | 'timeout'
 * @param {string} params.source - откуда вызван: 'city' | 'courier_address' | 'post_address'
 */
export function captureAutocompleteEmptyResult({
  context,
  query,
  responseTimeMs,
  errorType,
  source,
}) {
  captureCheckoutEvent({
    eventId: 'checkout.autocomplete.empty_result',
    level: 'warning',
    context,
    extraFields: {
      query: query?.substring(0, 100), // ограничиваем длину для PII
      response_time_ms: responseTimeMs,
      error_type: errorType,
      source,
    },
  })
}

/**
 * checkout.autocomplete.missing_required_fields
 * Автокомплит вернул данные, но обязательные поля оказались пустыми.
 *
 * @param {Object} params
 * @param {Object} params.context - контекст checkout
 * @param {string} params.query - что вводил пользователь
 * @param {string[]} params.missingFields - массив отсутствующих полей ['city', 'area']
 * @param {string} params.source - откуда вызван: 'city' | 'courier_address' | 'post_address'
 */
export function captureAutocompleteMissingFields({
  context,
  query,
  missingFields,
  source,
}) {
  captureCheckoutEvent({
    eventId: 'checkout.autocomplete.missing_required_fields',
    level: 'warning',
    context,
    extraFields: {
      query: query?.substring(0, 100),
      missing_fields: missingFields,
      source,
    },
  })
}

/**
 * checkout.autocomplete.city_modal_shown
 * Пользователю показано модальное окно смены города в цикле.
 * При show_count >= 3 — признак цикла, level='error'.
 *
 * @param {Object} params
 * @param {Object} params.context - контекст checkout
 * @param {number} params.showCount - сколько раз за сессию показано
 * @param {string} params.oldCity - текущий город
 * @param {string} params.newCity - предлагаемый город
 */
export function captureCityModalShown({
  context,
  showCount,
  oldCity,
  newCity,
}) {
  const isAlert = showCount >= 3

  captureCheckoutEvent({
    eventId: isAlert
      ? 'checkout.autocomplete.city_modal_shown_error'
      : 'checkout.autocomplete.city_modal_shown_info',
    level: isAlert ? 'error' : 'info',
    context,
    extraFields: {
      show_count: showCount,
      old_city: oldCity,
      new_city: newCity,
      is_cycle_alert: isAlert,
      'tag:show_count': String(showCount),
      'tag:is_cycle_alert': String(isAlert),
    },
  })
}

// ===========================================
// SESSION COUNTERS (для отслеживания циклов)
// ===========================================

const sessionCounters = {
  cityModalShown: 0,
  addressEdited: 0,
}

const requestTimestamps = new Map() // для отслеживания duplicate requests
const MAX_TRACKED_ENDPOINTS = 50

/**
 * Увеличивает и возвращает счётчик показов модального окна смены города
 */
export function incrementCityModalCounter() {
  sessionCounters.cityModalShown += 1
  return sessionCounters.cityModalShown
}

/**
 * Увеличивает счётчик редактирований адреса и отправляет событие при достижении порога. Используется в компонентах курьерки и почты.
 * @param {Object} context - контекст checkout (опционально, для автоматической отправки события)
 * @returns {number} текущее значение счётчика
 */
export function incrementAddressEditCounter(context = null) {
  sessionCounters.addressEdited += 1
  const editCount = sessionCounters.addressEdited
  // Автоматически отправляем событие при достижении порога
  if (context && editCount === 50) {
    captureAddressEditedRepeatedly({ context, editCount })
  }

  return editCount
}

// ===========================================
// DELIVERY API EVENTS
// ===========================================

/**
 * checkout.delivery_api.request_error
 * Ошибка при запросе к API доставки — недоступность или невалидный ответ.
 *
 * @param {Object} params
 * @param {Object} params.context - контекст checkout
 * @param {string} params.endpoint - endpoint API ('/delivery', '/pickpoints', etc.)
 * @param {string} params.errorCode - код ошибки
 * @param {number} params.responseStatus - HTTP статус ответа
 */
export function captureDeliveryApiError({
  context,
  endpoint,
  errorCode,
  responseStatus,
}) {
  captureCheckoutEvent({
    eventId: 'checkout.delivery_api.request_error',
    level: 'error',
    context,
    extraFields: {
      endpoint,
      error_code: errorCode,
      response_status: responseStatus,
    },
  })
}

/**
 * checkout.api.duplicate_request
 * Повторный запрос к одному и тому же endpoint подряд — признак цикла.
 * Алерт при repeat_count >= 5.
 *
 * @param {Object} params
 * @param {Object} params.context - контекст checkout
 * @param {string} params.endpoint - endpoint API
 * @param {number} params.repeatCount - количество повторов
 * @param {number} params.intervalMs - интервал между запросами в мс
 */
export function captureDuplicateApiRequest({
  context,
  endpoint,
  repeatCount,
  intervalMs,
}) {
  const isAlert = repeatCount >= 5

  captureCheckoutEvent({
    eventId: 'checkout.api.duplicate_request',
    level: isAlert ? 'error' : 'warning',
    context,
    extraFields: {
      endpoint,
      repeat_count: repeatCount,
      interval_ms: intervalMs,
      is_cycle_alert: isAlert,
      'tag:repeat_count': String(repeatCount),
      'tag:is_cycle_alert': String(isAlert),
    },
  })
}

export function captureBasketApiError({ context, errorCode, responseStatus }) {
  const code = String(errorCode ?? 'unknown').slice(0, 200)

  captureCheckoutEvent({
    eventId: 'checkout.basket.api_error',
    level: 'warning',
    context,
    extraFields: {
      endpoint: '/basket',
      error_code: code,
      response_status: responseStatus ?? null,
      'tag:error_code': code.slice(0, TAG_VALUE_MAX_LENGTH),
    },
  })
}

/**
 * Проверяет и отслеживает повторные запросы
 * @returns {Object} { isDuplicate, repeatCount, intervalMs }
 */
export function trackApiRequest(endpoint) {
  const now = Date.now()

  // Очистка при превышении лимита
  if (requestTimestamps.size >= MAX_TRACKED_ENDPOINTS) {
    const firstKey = requestTimestamps.keys().next().value
    requestTimestamps.delete(firstKey)
  }

  const lastRequest = requestTimestamps.get(endpoint)
  if (!lastRequest) {
    requestTimestamps.set(endpoint, { timestamp: now, count: 1 })
    return { isDuplicate: false, repeatCount: 1, intervalMs: 0 }
  }

  const intervalMs = now - lastRequest.timestamp
  const isDuplicate = intervalMs < 5000 // 5 секунд — порог для определения повтора
  if (isDuplicate) {
    lastRequest.count += 1
    lastRequest.timestamp = now
    return { isDuplicate: true, repeatCount: lastRequest.count, intervalMs }
  }

  lastRequest.count = 1
  lastRequest.timestamp = now
  return { isDuplicate: false, repeatCount: 1, intervalMs }
}

// ===========================================
// PAYMENT EVENTS
// ===========================================

/**
 * checkout.payment.provider_init_error
 * Ошибка при инициализации платёжного провайдера.
 *
 * @param {Object} params
 * @param {Object} params.context - контекст checkout
 * @param {string} params.provider - название провайдера
 * @param {string} params.errorCode - код ошибки
 * @param {string} params.errorMessage - сообщение ошибки
 */
export function capturePaymentProviderInitError({
  context,
  provider,
  errorCode,
  errorMessage,
}) {
  captureCheckoutEvent({
    eventId: 'checkout.payment.provider_init_error',
    level: 'error',
    context,
    extraFields: {
      provider,
      error_code: errorCode,
      error_message: errorMessage?.substring(0, 200),
    },
  })
}

const TAG_VALUE_MAX_LENGTH = 200

/**
 * Дублирует код и текст ошибки тегами error_code / failure_reason.
 * Extra-поля поиском Sentry не индексируются, теги — индексируются, можно искать по поиску
 */
function searchableErrorTags({ failureReason, errorCode }) {
  const tags = {}
  const code = errorCode == null ? '' : String(errorCode).trim()
  const reason = failureReason == null ? '' : String(failureReason).trim()
  if (code) tags['tag:error_code'] = code.slice(0, TAG_VALUE_MAX_LENGTH)
  if (reason) tags['tag:failure_reason'] = reason.slice(0, TAG_VALUE_MAX_LENGTH)
  return tags
}

/**
 * Достаёт текст и код ошибки из того, что прилетело в catch после неудачного запроса.
 * @param {*} err - аргумент catch
 * @returns {{ error: string|undefined, errorTitle: string|undefined }}
 */
export function extractApiError(err) {
  if (typeof err === 'string' || err instanceof String) {
    return { error: String(err), errorTitle: err.errorTitle }
  }
  const data = err?.response?.data || err?.response
  return { error: data?.error, errorTitle: data?.errorTitle }
}

/**
 * checkout.payment.transaction_failed_technical_postOrder
 * Пользователь не смог завершить транзакцию по технической причине.
 *
 * @param {Object} params
 * @param {Object} params.context - контекст checkout
 * @param {string} params.provider - название провайдера
 * @param {string} params.failureReason - причина отказа
 * @param {string} params.errorCode - код ошибки
 */
export function capturePaymentTransactionFailed({
  context,
  provider,
  failureReason,
  errorCode,
}) {
  captureCheckoutEvent({
    eventId: 'checkout.payment.transaction_failed_technical_postOrder',
    level: 'error',
    context,
    extraFields: {
      provider,
      failure_reason: failureReason,
      error_code: errorCode,
      ...searchableErrorTags({ failureReason, errorCode }),
    },
  })
}

/**
 * checkout.order.validation_failed_postOrder
 * Если бэкенд отклонил оформление заказа ошибкой в теле успешного (HTTP 200)ответа
 * @param {Object} params
 * @param {Object} params.context - контекст checkout
 * @param {string} params.provider - активный платёжный тип
 * @param {string} params.failureReason - текст ошибки (response.data.error)
 * @param {string} params.errorCode - код ошибки (response.data.errorTitle)
 */
export function captureOrderValidationFailed({
  context,
  provider,
  failureReason,
  errorCode,
}) {
  captureCheckoutEvent({
    eventId: 'checkout.order.validation_failed_postOrder',
    level: 'error',
    context,
    extraFields: {
      provider,
      failure_reason: failureReason,
      error_code: errorCode,
      ...searchableErrorTags({ failureReason, errorCode }),
    },
  })
}

// ===========================================
// BEHAVIOR EVENTS
// ===========================================

/**
 * checkout.behavior.address_edited_repeatedly
 * Пользователь редактировал адрес и ввел более 50 символов.
 * Алерт при edit_count >= 50.
 *
 * @param {Object} params
 * @param {Object} params.context - контекст checkout
 * @param {number} params.editCount - количество редактирований
 */
export function captureAddressEditedRepeatedly({ context, editCount }) {
  captureCheckoutEvent({
    eventId: 'checkout.behavior.address_edited_repeatedly',
    level: 'warning',
    context,
    extraFields: {
      edit_count: editCount,
      is_cycle_alert: true,
      'tag:edit_count': String(editCount),
      'tag:is_cycle_alert': 'true',
    },
  })
}

/**
 * checkout.behavior.long_time_on_address_step
 * Пользователь провёл на шаге адреса аномально долго.
 * Алерт при time_spent_sec >= 180 (3 минуты).
 *
 * @param {Object} params
 * @param {Object} params.context - контекст checkout
 * @param {number} params.timeSpentSec - время в секундах
 */
export function captureLongTimeOnAddressStep({ context, timeSpentSec }) {
  const isAlert = timeSpentSec >= 180

  captureCheckoutEvent({
    eventId: 'checkout.behavior.long_time_on_address_step',
    level: isAlert ? 'warning' : 'info',
    context,
    extraFields: {
      time_spent_sec: timeSpentSec,
      is_alert: isAlert,
      'tag:time_spent_sec': String(timeSpentSec),
      'tag:is_alert': String(isAlert),
    },
  })
}

// ===========================================
// PROFILE EVENTS
// ===========================================

/**
 * checkout.profile.address_empty
 * Поле адреса пришло из профиля пустым — до срабатывания любой валидации.
 *
 * @param {Object} params
 * @param {Object} params.context - контекст checkout
 * @param {string[]} params.emptyFields - массив пустых полей
 */
export function captureProfileAddressEmpty({ context, emptyFields }) {
  captureCheckoutEvent({
    eventId: 'checkout.profile.address_empty',
    level: 'warning',
    context,
    extraFields: {
      empty_fields: emptyFields,
    },
  })
}

// ===========================================
// ADDRESS STEP TIME TRACKER
// ===========================================

let addressStepStartTime = null

/**
 * Начинает отсчёт времени на шаге адреса
 */
export function startAddressStepTimer() {
  addressStepStartTime = Date.now()
}

/**
 * Возвращает время на шаге адреса в секундах
 */
export function getAddressStepTime() {
  if (!addressStepStartTime) return 0
  return Math.floor((Date.now() - addressStepStartTime) / 1000)
}
